import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useSearchParams, useLocation } from 'react-router-dom';
import { CssBaseline, ThemeProvider, createTheme } from '@mui/material';
import { Suspense } from 'react';
import { Analytics } from '@vercel/analytics/react';
import { Layout } from './components/layout/Layout';
import ErrorBoundary from './components/common/ErrorBoundary';
import LoginPage from './features/auth/pages/LoginPage';
import ProtectedRoute from './components/auth/ProtectedRoute';

import SmartRedirect from './components/auth/SmartRedirect';
import { AuthProvider } from './shared/contexts/AuthContext';
import { NotificationProvider } from './shared/contexts/NotificationContext';
import { SessionExpirationProvider } from './shared/contexts/SessionExpirationContext';
import { activityTracker } from './shared/services/activityTracker';
import { SocketProvider } from './contexts/SocketContext';
import { PttProvider } from './contexts/PttContext';
import { VoiceCallBootstrap } from './components/twilio-ccp/VoiceCallBootstrap';
import { GlobalPttBootstrap } from './components/ptt/GlobalPttBootstrap';
import PaymentNotification from './components/common/PaymentNotification';
import { CWYLoader } from './components/common/CWYLoader';
import PageAccessRoute from './components/auth/PageAccessRoute';
import AdminRoute from './components/auth/AdminRoute';
import { useSentryRouteTags } from './hooks/useSentryRouteTags';
import { usePageNavTracker } from './hooks/usePageNavTracker';
import lazyWithRetry from './utils/lazyWithRetry';
// Import test utility for browser console testing (only in development)
if (import.meta.env.DEV) {
  import('./utils/testSessionExpiration');
}

// Lazy load all pages with retry and auto-refresh on chunk load failures
// This handles the common issue when new deployments invalidate old chunk files
// LeadsPage removed - leads feature deprecated
const LeadDetailPage = lazyWithRetry(() => import('./features/leads/pages/LeadDetailPage'));
const ChatsListPage = lazyWithRetry(() =>
  import('./features/clients/pages/ClientChatsPage').then((m) => ({ default: m.ChatsListPage }))
);
const ChatsDashboardPage = lazyWithRetry(() => import('./features/clients/pages/ChatsDashboardPage'));
const SupervisorActionBoardPage = lazyWithRetry(() => import('./features/contact-center/pages/SupervisorActionBoardPage'));
const ChatTopicsPage = lazyWithRetry(() => import('./features/clients/pages/ChatTopicsPage'));
const MyChatsPageRoute = lazyWithRetry(() =>
  import('./features/clients/pages/ClientChatsPage').then((m) => ({ default: m.MyChatsPage }))
);
const ClientChatsLegacyRedirect = lazyWithRetry(() => import('./features/clients/pages/ClientChatsPage'));
const ContactChatsPage = lazyWithRetry(() => import('./features/contacts/pages/ContactChatsPage'));
const AgentPhonePage = lazyWithRetry(() => import('./features/agent-phone/pages/AgentPhonePage'));
const ClientsPage = lazyWithRetry(() => import('./features/clients/pages/ClientsPage'));
const NewsletterClientsPage = lazyWithRetry(() => import('./features/clients/pages/NewsletterClientsPage').then(m => ({ default: m.NewsletterClientsPage })));
const ServiceOrdersPage = lazyWithRetry(() => import('./features/service-orders/pages/ServiceOrdersPage'));
const PaymentsPage = lazyWithRetry(() => import('./features/payments/pages/PaymentsPage'));
const ExpensesToTransactionsRedirect = lazyWithRetry(
  () => import('./features/payments/pages/ExpensesToTransactionsRedirect')
);
const RazorpayPaymentLinksPage = lazyWithRetry(() => import('./features/payments/pages/RazorpayPaymentLinksPage'));
const CollectionsPaymentLinksPage = lazyWithRetry(
  () => import('./features/ledger/pages/CollectionsPaymentLinksPage')
);
const OutflowPage = lazyWithRetry(() => import('./features/outflow/pages/OutflowPage'));
const DocumentsPage = lazyWithRetry(() => import('./features/documents/pages/DocumentsPage'));
const ClientDocOcrQueuePage = lazyWithRetry(() => import('./features/documents/pages/ClientDocOcrQueuePage'));
const DocumentIngestQueuePage = lazyWithRetry(() => import('./features/documents/pages/DocumentIngestQueuePage'));
const SupportingDocumentsPage = lazyWithRetry(() => import('./features/ledger/pages/SupportingDocumentsPage'));
const DocumentsTrashPage = lazyWithRetry(() => import('./features/documents/pages/DocumentsTrashPage'));
const OcrQueuePage = lazyWithRetry(() => import('./features/ledger/pages/OcrQueuePage'));
const NotificationsPage = lazyWithRetry(() => import('./features/notifications/pages/NotificationsPage'));
const MailboxPage = lazyWithRetry(() => import('./features/mailbox/pages/MailboxPage'));
const ClientMailboxDirectoryPage = lazyWithRetry(() => import('./features/mailbox/pages/ClientMailboxDirectoryPage'));
const ClientMailboxViewerPage = lazyWithRetry(() => import('./features/mailbox/pages/ClientMailboxViewerPage'));
const AdminMailboxManagementPage = lazyWithRetry(() => import('./features/mailbox/pages/AdminMailboxManagementPage'));
const MeetingsPage = lazyWithRetry(() => import('./features/meetings/pages/MeetingsPage'));
const CalendarPage = lazyWithRetry(() => import('./features/calendar/pages/CalendarPage'));
const CalendarTablePage = lazyWithRetry(() => import('./features/calendar/pages/CalendarTablePage'));
const UnavailabilityPage = lazyWithRetry(() => import('./features/calendar/pages/UnavailabilityPage'));
const ErrorPage = lazyWithRetry(() => import('./shared/pages/ErrorPage'));
const loadHomePage = () => import('./shared/pages/HomePage');
const HomePage = lazyWithRetry(loadHomePage);
// Start Home while auth is still in flight so React remounts after setUser
// reuse a completed module instead of aborting a brand-new import().
if (typeof window !== 'undefined' && localStorage.getItem('token')) {
  void loadHomePage();
}
const ServiceOrderDetailPage = lazyWithRetry(() => import('./features/service-orders/pages/ServiceOrderDetailPage'));
const TermsOfServicePage = lazyWithRetry(() => import('./shared/pages/TermsOfServicePage'));
const PrivacyPolicyPage = lazyWithRetry(() => import('./shared/pages/PrivacyPolicyPage'));
const DataDeletionPage = lazyWithRetry(() => import('./shared/pages/DataDeletionPage'));
const ManageServicesPage = lazyWithRetry(() => import('./features/services/pages/ManageServices'));
const ServiceDetailPage = lazyWithRetry(() => import('./features/services/pages/ServiceDetailPage'));
const ManageUsersPage = lazyWithRetry(() => import('./features/users/pages/ManageUsers'));
const UserDetailPage = lazyWithRetry(() => import('./features/users/pages/UserDetailPage'));
const HrProfilesPage = lazyWithRetry(() => import('./features/users/pages/HrProfilesPage'));
const HrProfileDetailPage = lazyWithRetry(() => import('./features/users/pages/HrProfileDetailPage'));
const ProfilePage = lazyWithRetry(() => import('./features/auth/pages/ProfilePage'));
const ConnectContinuePage = lazyWithRetry(() => import('./features/auth/pages/ConnectContinuePage'));
const WhatsAppSettingsPage = lazyWithRetry(() => import('./features/settings/pages/WhatsAppSettingsPage'));
const OfficeManagementPage = lazyWithRetry(() => import('./features/settings/pages/OfficeManagementPage'));
const DepartmentListPage = lazyWithRetry(() => import('./features/departments/pages/DepartmentListPage'));
const DepartmentDetailPage = lazyWithRetry(() => import('./features/departments/pages/DepartmentDetailPage'));
const CreateDepartmentPage = lazyWithRetry(() => import('./features/departments/pages/CreateDepartmentPage'));
const WalkInsPage = lazyWithRetry(() => import('./features/walk-ins/pages/WalkInsPage'));
const ConversionsPage = lazyWithRetry(() => import('./features/conversions/pages/ConversionsPage'));
const DailyActivityLogPage = lazyWithRetry(() => import('./features/walk-ins/pages/DailyActivityLogPage'));
const DailyWorkReportPage = lazyWithRetry(() => import('./features/walk-ins/pages/DailyWorkReportPage'));
const WalkInAgentReportsPage = lazyWithRetry(() => import('./features/walk-ins/pages/WalkInAgentReportsPage'));
const WalkInOfficeReportsPage = lazyWithRetry(() => import('./features/walk-ins/pages/WalkInOfficeReportsPage'));
const WorkspaceAdminPage = lazyWithRetry(() => import('./features/workspace/pages/WorkspaceAdminPage'));
const MyDocumentsPage = lazyWithRetry(() => import('./features/workspace/pages/MyDocumentsPage'));
const DocumentRegisterPage = lazyWithRetry(() => import('./features/workspace/pages/DocumentRegisterPage'));
const DocumentRegisterDetailPage = lazyWithRetry(() => import('./features/workspace/pages/DocumentRegisterDetailPage'));
const DailyConversionLogPage = lazyWithRetry(() => import('./features/service-orders/pages/DailyConversionLogPage'));
const MyCasesPage = lazyWithRetry(() => import('./features/service-orders/pages/MyCasesPage'));
const MyCasesRateListPage = lazyWithRetry(() => import('./features/service-orders/pages/MyCasesRateListPage'));
const OrderDashboardPage = lazyWithRetry(() => import('./features/service-orders/pages/OrderDashboardPage'));
const ServiceRatesPage = lazyWithRetry(() => import('./features/service-orders/pages/ServiceRatesPage'));
const OrdersByServicePage = lazyWithRetry(() => import('./features/service-orders/pages/OrdersByServicePage'));
const OrdersByCaseManagerPage = lazyWithRetry(() => import('./features/service-orders/pages/OrdersByCaseManagerPage'));
const CanadaOrdersPage = lazyWithRetry(() => import('./features/service-orders/pages/CanadaOrdersPage'));
const IndiaOrdersPage = lazyWithRetry(() => import('./features/service-orders/pages/IndiaOrdersPage'));
const UnlinkedRevenueCollectionPage = lazyWithRetry(
  () => import('./features/service-orders/pages/UnlinkedRevenueCollectionPage')
);
const ReportsDashboardPage = lazyWithRetry(() => import('./features/reports/pages/ReportsDashboardPage'));
const WorkspaceSettingsDashboardPage = lazyWithRetry(() => import('./features/settings/pages/WorkspaceSettingsDashboardPage'));
const ShippingPage = lazyWithRetry(() => import('./features/shipping/pages/ShippingPage'));
const CWYTokenDashboardPage = lazyWithRetry(() => import('./features/token/pages/CWYTokenDashboardPage'));
const ManageAlertsPage = lazyWithRetry(() => import('./features/settings/pages/ManageAlertsPage'));
const AppFrontendHubPage = lazyWithRetry(() => import('./features/app-frontend/pages/AppFrontendHubPage'));
const CwyAtoZAppHubPage = lazyWithRetry(() => import('./features/staff-mobile/pages/CwyAtoZAppHubPage'));
const AtoZPageAccessListPage = lazyWithRetry(() => import('./features/staff-mobile/pages/AtoZPageAccessListPage'));
const AtoZPageAccessUserPage = lazyWithRetry(() => import('./features/staff-mobile/pages/AtoZPageAccessUserPage'));
const MembershipSettingsPage = lazyWithRetry(() => import('./features/membership/pages/MembershipSettingsPage'));
const EsimPlansPage = lazyWithRetry(() => import('./features/esim/pages/EsimPlansPage'));
const StoreListingsPage = lazyWithRetry(() => import('./features/app-frontend/pages/StoreListingsPage'));
const HomeItemsPage = lazyWithRetry(() => import('./features/app-frontend/pages/HomeItemsPage'));
const ConsultantListingsPage = lazyWithRetry(() => import('./features/app-frontend/pages/ConsultantListingsPage'));
const ProfessionalsPage = lazyWithRetry(() => import('./features/app-frontend/pages/ProfessionalsPage'));
const HomeScreenCarouselPage = lazyWithRetry(() => import('./features/app-frontend/pages/HomeScreenCarouselPage'));
const WorkloadManagementPage = lazyWithRetry(() => import('./features/settings/pages/WorkloadManagementPage'));
const TeamMemberWorkloadDetailPage = lazyWithRetry(() => import('./features/settings/pages/TeamMemberWorkloadDetailPage'));
const SignInLogsPage = lazyWithRetry(() => import('./features/settings/pages/SignInLogsPage'));
const PageUsageStatsPage = lazyWithRetry(() => import('./features/settings/pages/PageUsageStatsPage'));
const AnalyticsPortalPage = lazyWithRetry(() => import('./features/dashboard/pages/AnalyticsPortalPage'));
const MeetCallLogsPage = lazyWithRetry(() => import('./features/settings/pages/MeetCallLogsPage'));
const OrderSectionPlaceholderPage = lazyWithRetry(() => import('./features/service-orders/pages/OrderSectionPlaceholderPage'));
const RefugeeControlTowerPage = lazyWithRetry(() => import('./features/refugee-control-tower/pages/RefugeeControlTowerPage'));
const SsOpsPage = lazyWithRetry(() => import('./features/ss-ops/pages/SsOpsPage'));
const TrControlTowerPage = lazyWithRetry(() => import('./features/tr-control-tower/pages/TrControlTowerPage'));
const ContactCenterPage = lazyWithRetry(() => import('./features/contact-center/pages/ContactCenterPage'));
const CallLogPage = lazyWithRetry(() => import('./features/contact-center/pages/CallLogPage'));
const OutgoingCallLogsPage = lazyWithRetry(() =>
  import('./features/contact-center/pages/CallLogPage').then((module) => ({
    default: module.OutgoingCallLogsPage
  }))
);
const MyCallsPage = lazyWithRetry(() => import('./features/contact-center/pages/MyCallsPage'));
const OpsDashboardPage = lazyWithRetry(() => import('./features/contact-center/pages/OpsDashboardPage'));
const ExtensionsPage = lazyWithRetry(() => import('./features/extensions/pages/ExtensionsPage'));
const LedgerDashboardPage = lazyWithRetry(() => import('./features/ledger/pages/LedgerDashboardPage'));
const LedgerPageAccessPage = lazyWithRetry(() => import('./features/ledger/pages/LedgerPageAccessPage'));
const GeneralLedgerPage = lazyWithRetry(() => import('./features/ledger/pages/GeneralLedgerPage'));
const AccountLedgerPage = lazyWithRetry(() => import('./features/ledger/pages/AccountLedgerPage'));
const BankStatementPage = lazyWithRetry(() => import('./features/ledger/pages/BankStatementPage'));
const ReconciliationPage = lazyWithRetry(() => import('./features/ledger/pages/ReconciliationPage'));
const CreditCardStatementsPage = lazyWithRetry(() => import('./features/ledger/pages/CreditCardStatementsPage'));
const CreditCardReconciliationPage = lazyWithRetry(
  () => import('./features/ledger/pages/CreditCardReconciliationPage')
);
const AccountsManagementPage = lazyWithRetry(() => import('./features/ledger/pages/AccountsManagementPage'));
const IntegrationsPage = lazyWithRetry(() => import('./features/ledger/pages/IntegrationsPage'));
const CompanyManagementPage = lazyWithRetry(() => import('./features/ledger/pages/CompanyManagementPage'));
const ManageGLAccountsPage = lazyWithRetry(() => import('./features/ledger/pages/ManageGLAccountsPage'));
const ChartOfAccountsPage = lazyWithRetry(() => import('./features/ledger/pages/ChartOfAccountsPage'));
const PayeeManagementPage = lazyWithRetry(() => import('./features/ledger/pages/PayeeManagementPage'));
const RecurringVendorChecklistPage = lazyWithRetry(
  () => import('./features/ledger/pages/RecurringVendorChecklistPage')
);
const StatementsRegisterPage = lazyWithRetry(() => import('./features/ledger/pages/StatementsRegisterPage'));
const LoanRegisterLegacyRedirect = lazyWithRetry(() => import('./features/ledger/pages/LoanRegisterLegacyRedirect'));
const InteracPaymentClaimsPage = lazyWithRetry(
  () => import('./features/ledger/pages/InteracPaymentClaimsPage')
);
const PartnerCollectionsPage = lazyWithRetry(
  () => import('./features/ledger/pages/PartnerCollectionsPage')
);
const TrialBalancePage = lazyWithRetry(() => import('./features/ledger/pages/TrialBalancePage'));
const IncomeStatementPage = lazyWithRetry(() => import('./features/ledger/pages/IncomeStatementPage'));
const BalanceSheetPage = lazyWithRetry(() => import('./features/ledger/pages/BalanceSheetPage'));
const FinancialStatementGeneratorPage = lazyWithRetry(
  () => import('./features/ledger/pages/FinancialStatementGeneratorPage')
);
const MonthlyGstHstBreakdownPage = lazyWithRetry(
  () => import('./features/ledger/pages/MonthlyGstHstBreakdownPage')
);
const ForecastPage = lazyWithRetry(() => import('./features/ledger/pages/ForecastPage'));
const FinancialStatementEditorPage = lazyWithRetry(
  () => import('./features/ledger/pages/FinancialStatementEditorPage')
);
const AccountsReceivablePage = lazyWithRetry(() => import('./features/ledger/pages/AccountsReceivablePage'));
const AccountsPayablePage = lazyWithRetry(() => import('./features/ledger/pages/AccountsPayablePage'));
const SalaryContractorPaymentsPage = lazyWithRetry(() => import('./features/ledger/pages/SalaryContractorPaymentsPage'));
const SalaryPayrollCalculatorPage = lazyWithRetry(
  () => import('./features/ledger/pages/SalaryPayrollCalculatorPage')
);
const PayrollPaySlipsListPage = lazyWithRetry(
  () => import('./features/ledger/pages/PayrollPaySlipsListPage')
);
const SalarySlipsLayoutPage = lazyWithRetry(
  () => import('./features/ledger/pages/SalarySlipsLayoutPage')
);
const PayrollTaxRemittancePage = lazyWithRetry(
  () => import('./features/ledger/pages/PayrollTaxRemittancePage')
);

function LegacySalaryPayrollRedirect() {
  const [searchParams] = useSearchParams();
  const q = searchParams.toString();
  return <Navigate to={`/app/ledger/books/salary-slips/create${q ? `?${q}` : ''}`} replace />;
}

function LegacyPayrollPayslipsRedirect() {
  const [searchParams] = useSearchParams();
  const q = searchParams.toString();
  return <Navigate to={`/app/ledger/books/salary-slips${q ? `?${q}` : ''}`} replace />;
}

function LegacyGstForecastRedirect() {
  const [searchParams] = useSearchParams();
  const q = searchParams.toString();
  return <Navigate to={`/app/ledger/gst-forecast${q ? `?${q}` : ''}`} replace />;
}
const FinancialDocumentsLayoutPage = lazyWithRetry(
  () => import('./features/ledger/pages/FinancialDocumentsLayoutPage')
);
const FinancialDocumentsIndexRedirect = lazyWithRetry(() =>
  import('./features/ledger/pages/FinancialDocumentsLayoutPage').then((m) => ({
    default: m.FinancialDocumentsIndexRedirect
  }))
);
const FinancialDocumentsInvoicesPage = lazyWithRetry(
  () => import('./features/ledger/pages/FinancialDocumentsInvoicesPage')
);
const FinancialDocumentsReceiptsPage = lazyWithRetry(
  () => import('./features/ledger/pages/FinancialDocumentsReceiptsPage')
);
const VoucherHubPage = lazyWithRetry(() => import('./features/ledger/pages/VoucherHubPage'));

function LedgerBillingLegacyRedirect() {
  const [sp] = useSearchParams();
  const q = sp.toString();
  return <Navigate to={`/app/ledger/financial-documents/invoices${q ? `?${q}` : ''}`} replace />;
}

function VoucherRootRedirect() {
  const [sp] = useSearchParams();
  const q = sp.toString();
  return <Navigate to={`payment${q ? `?${q}` : ''}`} replace />;
}

function LegacyFinancialDocumentsVouchersRedirect() {
  const [sp] = useSearchParams();
  const q = sp.toString();
  return <Navigate to={`/app/ledger/vouchers/payment${q ? `?${q}` : ''}`} replace />;
}

/** Old /app/service-orders URLs → /app/orders-dashboard (bookmarks & shared links). */
function ServiceOrdersLegacyRedirect() {
  const location = useLocation();
  const prefix = '/app/service-orders';
  const pathAfter = location.pathname.startsWith(prefix) ? location.pathname.slice(prefix.length) : '';
  const to = `/app/orders-dashboard${pathAfter}`;
  return <Navigate to={`${to}${location.search}`} replace />;
}
const GLAccountsPage = lazyWithRetry(() => import('./features/ledger/pages/GLAccountsPage'));
const JournalListPage = lazyWithRetry(() => import('./features/ledger/pages/JournalListPage'));
const AccountingExceptionsPage = lazyWithRetry(() => import('./features/ledger/pages/AccountingExceptionsPage'));
const AccountingExceptionsRedirect = lazyWithRetry(() => import('./features/ledger/pages/AccountingExceptionsRedirect'));
const JournalDetailPage = lazyWithRetry(() => import('./features/ledger/pages/JournalDetailPage'));
const TasksPage = lazyWithRetry(() => import('./features/tasks/pages/TasksPage'));
const TaskManagerPage = lazyWithRetry(() => import('./features/tasks/pages/TaskManagerPage'));
const FormsPage = lazyWithRetry(() => import('./features/forms/pages/FormsPage'));
const FormsManagementPage = lazyWithRetry(() => import('./features/forms/pages/FormsManagementPage'));
const FormBuilderPage = lazyWithRetry(() => import('./features/forms/pages/FormBuilderPage'));
// Legacy sheets imports removed
const TeamChatPage = lazyWithRetry(() => import('./features/team-chat/pages/TeamChatPage'));
const TeamChatPageGroups = lazyWithRetry(() => import('./features/team-chat/pages/TeamChatsPage'));
const TeamChatDmPage = lazyWithRetry(() => import('./features/team-chat/pages/TeamChatDmPage'));
const TeamChatCaseChatsPage = lazyWithRetry(() => import('./features/team-chat/pages/TeamChatCaseChatsPage'));
const AllCommunityPage = lazyWithRetry(() => import('./features/clients/pages/AllCommunityPage'));
const MyCommunityPage = lazyWithRetry(() => import('./features/clients/pages/MyCommunityPage'));
const CommunityRootRedirect = lazyWithRetry(() => import('./features/community/pages/CommunityRootRedirect'));
const CommunityJoinPage = lazyWithRetry(() => import('./features/community/pages/CommunityJoinPage'));
const PageAccessMatrixPage = lazyWithRetry(() => import('./features/settings/pages/PageAccessMatrixPage'));
const AIAssistantPage = lazyWithRetry(() => import('./features/ai-assistant/pages/AIAssistantPage'));
const AIKnowledgePage = lazyWithRetry(() => import('./features/ai-knowledge/pages/AIKnowledgePage'));
const AIUsageAdminPage = lazyWithRetry(() => import('./features/ai-assistant/pages/AIUsageAdminPage'));
const LegalTasksDashboardPage = lazyWithRetry(() => import('./features/legal-tasks/pages/LegalTasksDashboardPage'));
const LegalTaskDetailPage = lazyWithRetry(() => import('./features/legal-tasks/pages/LegalTaskDetailPage'));
const MyTasksPage = lazyWithRetry(() => import('./features/legal-tasks/pages/MyTasksPage'));
const HRAttendancePage = lazyWithRetry(() => import('./features/attendance/pages/HRAttendancePage'));
const MyAttendancePage = lazyWithRetry(() => import('./features/attendance/pages/MyAttendancePage'));
const HRTrainingPage = lazyWithRetry(() => import('./features/training/pages/HRTrainingPage'));
const MyTrainingPage = lazyWithRetry(() => import('./features/training/pages/MyTrainingPage'));
const MyTrainingDetailPage = lazyWithRetry(() => import('./features/training/pages/MyTrainingDetailPage'));
const HRTrainingDetailPage = lazyWithRetry(() => import('./features/training/pages/HRTrainingDetailPage'));
const HRPolicyPage = lazyWithRetry(() => import('./features/hr-policy/pages/HRPolicyPage'));
const HRPolicyDetailPage = lazyWithRetry(() => import('./features/hr-policy/pages/HRPolicyDetailPage'));
const WorkComplianceCaseDetailPage = lazyWithRetry(
  () => import('./features/hr-policy/pages/WorkComplianceCaseDetailPage')
);
const MyPoliciesPage = lazyWithRetry(() => import('./features/hr-policy/pages/MyPoliciesPage'));
const MyPolicyDetailPage = lazyWithRetry(() => import('./features/hr-policy/pages/MyPolicyDetailPage'));
const MyCompliancePage = lazyWithRetry(() => import('./features/hr-policy/pages/MyCompliancePage'));
const MyComplianceDetailPage = lazyWithRetry(
  () => import('./features/hr-policy/pages/MyComplianceDetailPage')
);
const PhotoJobsPage = lazyWithRetry(() => import('./features/photo-jobs/pages/PhotoJobsPage'));
const PhotoJobDetailPage = lazyWithRetry(() => import('./features/photo-jobs/pages/PhotoJobDetailPage'));
const PhotoJobDeletePage = lazyWithRetry(() => import('./features/photo-jobs/pages/PhotoJobDeletePage'));


// Loading fallback component with custom CWY loader
const LoadingFallback = () => <CWYLoader fullScreen size={100} />;

// Create a custom theme - ensure it's created after all imports
function createAppTheme() {
  return createTheme({
  palette: {
    primary: {
      main: '#ff0000', // YouTube Red
      light: '#ff4444',
      dark: '#cc0000',
    },
    secondary: {
      main: '#606060', // YouTube Grey
      light: '#909090',
      dark: '#404040',
    },
    background: {
      default: '#ffffff',
      paper: '#f8f9fa',
    },
    text: {
      primary: '#0f0f0f', // YouTube Dark Text
      secondary: '#606060', // YouTube Grey Text
    },
    grey: {
      50: '#f8f9fa',
      100: '#f1f3f4',
      200: '#e8eaed',
      300: '#dadce0',
      400: '#bdc1c6',
      500: '#9aa0a6',
      600: '#80868b',
      700: '#5f6368',
      800: '#3c4043',
      900: '#202124',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Inter", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 500,
      color: '#0f0f0f',
    },
    h2: {
      fontWeight: 500,
      color: '#0f0f0f',
    },
    h3: {
      fontWeight: 500,
      color: '#0f0f0f',
    },
    h4: {
      fontWeight: 500,
      color: '#0f0f0f',
    },
    h5: {
      fontWeight: 500,
      color: '#0f0f0f',
    },
    h6: {
      fontWeight: 500,
      color: '#0f0f0f',
    },
    body1: {
      color: '#0f0f0f',
    },
    body2: {
      color: '#606060',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 18,
          textTransform: 'none',
          fontWeight: 500,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
          },
        },
        contained: {
          backgroundColor: '#f1f3f4',
          color: '#0f0f0f',
          '&:hover': {
            backgroundColor: '#e8eaed',
          },
        },
        outlined: {
          borderColor: '#dadce0',
          color: '#0f0f0f',
          '&:hover': {
            backgroundColor: '#f8f9fa',
            borderColor: '#dadce0',
          },
        },
        text: {
          color: '#0f0f0f',
          '&:hover': {
            backgroundColor: '#f8f9fa',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
          border: '1px solid #e8eaed',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          borderRadius: 18,
          ...(ownerState.variant !== 'outlined' && (!ownerState.color || ownerState.color === 'default')
            ? { backgroundColor: '#f1f3f4', color: '#0f0f0f' }
            : {}),
        }),
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          color: '#606060',
          textTransform: 'none',
          fontWeight: 500,
          '&.Mui-selected': {
            color: '#0f0f0f',
          },
          '&:hover': {
            color: '#0f0f0f',
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: '#0f0f0f',
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: '#0f0f0f',
          textDecoration: 'none',
          '&:hover': {
            color: '#606060',
          },
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          '&.MuiTypography-colorPrimary': {
            color: '#0f0f0f',
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: '#606060',
          '&.Mui-focused': {
            color: '#0f0f0f',
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: '#606060',
          '&.Mui-focused': {
            color: '#0f0f0f',
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#dadce0',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#0f0f0f',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#0f0f0f',
          },
        },
      },
    },
    MuiCircularProgress: {
      styleOverrides: {
        root: {
          color: '#606060',
        },
      },
    },
    MuiSnackbar: {
      defaultProps: {
        anchorOrigin: { vertical: 'top', horizontal: 'center' },
      },
      styleOverrides: {
        root: {
          zIndex: 10000,
          '&.MuiSnackbar-anchorOriginTopCenter': {
            top: 72,
          },
        },
      },
    },
  },
  shape: {
    borderRadius: 8,
  },
  });
}

// Initialize theme once
const theme = createAppTheme();

const App: React.FC = () => {
  useEffect(() => {
    // Start activity tracking when app load
    activityTracker.startTracking();

    // Cleanup when app unmounts
    return () => {
      activityTracker.cleanup();
    };
  }, []);

  return (
    <SocketProvider>
      <PttProvider>
      <AuthProvider>
        <SessionExpirationProvider>
        <NotificationProvider>
          <ErrorBoundary>
            <ThemeProvider theme={theme}>
              <CssBaseline />
              <Router>
                <AppRoutes />
              </Router>
              <Analytics />
            </ThemeProvider>
          </ErrorBoundary>
        </NotificationProvider>
        </SessionExpirationProvider>
      </AuthProvider>
      </PttProvider>
    </SocketProvider>
  );
};

// Component to handle route-based Sentry tags (must be inside Router)
const AppRoutes: React.FC = () => {
  useSentryRouteTags(); // Set feature tags based on current route
  usePageNavTracker();
  
  return (
    <>
      <VoiceCallBootstrap />
      <GlobalPttBootstrap />
      <PaymentNotification />
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
                    <Route
                      path="/app/agent-phone"
                      element={
                        <ProtectedRoute>
                          <AgentPhonePage />
                        </ProtectedRoute>
                      }
                    />
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/privacy" element={<PrivacyPolicyPage />} />
                    <Route path="/terms-of-service" element={<TermsOfServicePage />} />
                    <Route path="/data-deletion" element={<DataDeletionPage />} />
                    <Route path="/app" element={
                      <ProtectedRoute>
                        <Layout />
                      </ProtectedRoute>
                    }>
                      <Route index element={<HomePage />} />
                      <Route path="orders-dashboard" element={<OrderDashboardPage />} />
                      <Route path="orders-dashboard/service-rates" element={<ServiceRatesPage />} />
                      <Route path="orders-dashboard/by-service" element={<OrdersByServicePage />} />
                      <Route path="orders-dashboard/by-case-manager" element={<OrdersByCaseManagerPage />} />
                      <Route path="orders-dashboard/canada" element={<CanadaOrdersPage />} />
                      <Route path="orders-dashboard/india" element={<IndiaOrdersPage />} />
                      <Route path="orders-dashboard/unlinked-revenue" element={<UnlinkedRevenueCollectionPage />} />
                      <Route path="workspace" element={<WorkspaceSettingsDashboardPage />} />
                      <Route path="settings/google-workspace" element={<WorkspaceAdminPage />} />
                      <Route path="settings/newsletter-board" element={<NewsletterClientsPage />} />
                      <Route path="workload" element={<WorkloadManagementPage />} />
                      <Route path="workload/team-members" element={<TeamMemberWorkloadDetailPage />} />
                      <Route path="workspace/sign-in-logs" element={<SignInLogsPage />} />
                      <Route path="workspace/page-usage" element={<PageUsageStatsPage />} />
                      <Route path="workspace/analytics" element={<AnalyticsPortalPage />} />
                      <Route path="workspace/meet-call-logs" element={<MeetCallLogsPage />} />
                      <Route path="workspace/user-working-hours" element={<Navigate to="/app/workspace/sign-in-logs" replace />} />
                      <Route path="workspace/page-access" element={<PageAccessMatrixPage />} />
                      <Route path="workspace/manage-alerts" element={<ManageAlertsPage />} />
                      <Route path="workspace/client-mailboxes" element={<ClientMailboxDirectoryPage />} />
                      <Route path="workspace/client-mailboxes/:clientId/:threadId?" element={<ClientMailboxViewerPage />} />
                      <Route path="workspace/admin-mailboxes" element={<AdminMailboxManagementPage />} />
                      <Route path="workspace/cwy-token" element={<CWYTokenDashboardPage />} />
                      <Route path="workspace/app-frontend" element={<AppFrontendHubPage />} />
                      <Route path="workspace/cwy-a-to-z-app" element={<CwyAtoZAppHubPage />} />
                      <Route path="workspace/cwy-a-to-z-app/page-access" element={<AtoZPageAccessListPage />} />
                      <Route
                        path="workspace/cwy-a-to-z-app/page-access/:userId"
                        element={<AtoZPageAccessUserPage />}
                      />
                      <Route path="workspace/membership" element={<MembershipSettingsPage />} />
                      <Route path="workspace/esim" element={<EsimPlansPage />} />
                      <Route path="workspace/app-frontend/store-listings" element={<StoreListingsPage />} />
                      <Route path="workspace/app-frontend/home-items" element={<HomeItemsPage />} />
                      <Route path="workspace/app-frontend/consultant-listings" element={<ConsultantListingsPage />} />
                      <Route path="workspace/app-frontend/professionals" element={<ProfessionalsPage />} />
                      <Route path="workspace/app-frontend/home-screen-carousel" element={<HomeScreenCarouselPage />} />
                      <Route path="workspace/document-register" element={<DocumentRegisterPage />} />
                      <Route path="workspace/document-register/:id" element={<DocumentRegisterDetailPage />} />
                      <Route path="workspace-settings" element={<Navigate to="/app/workspace" replace />} />
                      <Route path="reports" element={<ReportsDashboardPage />} />
                      <Route path="reports/daily-work-report" element={<DailyWorkReportPage />} />
                      <Route path="reports/daily-conversion-log" element={<DailyConversionLogPage />} />
                      <Route path="reports/walk-ins-by-agent" element={<WalkInAgentReportsPage />} />
                      <Route path="reports/walk-ins-by-office" element={<WalkInOfficeReportsPage />} />
                      <Route path="my-cases" element={<MyCasesPage />} />
                      <Route path="my-cases/rate-list" element={<MyCasesRateListPage />} />
                      <Route path="my-documents" element={<MyDocumentsPage />} />
                      <Route path="orders-dashboard/list" element={<ServiceOrdersPage />} />
                      <Route path="orders-dashboard/my-cases" element={<Navigate to="/app/my-cases" replace />} />
                      <Route path="photo-jobs" element={<PhotoJobsPage />} />
                      <Route path="photo-jobs/:jobId" element={<PhotoJobDetailPage />} />
                      <Route path="photo-jobs/:jobId/delete" element={<PhotoJobDeletePage />} />
                      <Route path="tasks" element={<TasksPage />} />
                      <Route path="tasks/manager" element={<TaskManagerPage />} />
                      {/* Leads route removed - leads feature deprecated */}
                      <Route path="clients" element={<ClientsPage />} />
                      <Route path="chats-list" element={<ChatsListPage />} />
                      <Route path="all-community" element={<AllCommunityPage />} />
                      <Route path="chats-list/community" element={<Navigate to="/app/all-community" replace />} />
                      <Route path="chats-dashboard" element={<ChatsDashboardPage />} />
                      <Route path="supervisor-action-board" element={<Navigate to="/app/contact-center/action-board" replace />} />
                      <Route path="chat-topics" element={<ChatTopicsPage />} />
                      <Route path="my-chat-assignments" element={<ChatTopicsPage />} />
                      <Route path="my-chats" element={<MyChatsPageRoute />} />
                      <Route path="my-community" element={<MyCommunityPage />} />
                      <Route path="my-chats/community" element={<Navigate to="/app/my-community" replace />} />
                      <Route path="client-chats/list" element={<Navigate to="/app/chats-list" replace />} />
                      <Route path="client-chats/my-chats" element={<Navigate to="/app/my-chats" replace />} />
                      <Route path="client-chats" element={<ClientChatsLegacyRedirect />} />
                      <Route path="team-chat" element={<TeamChatPage />} />
                      <Route path="team-chat/groups" element={<TeamChatPageGroups />} />
                      <Route path="team-chat/dm" element={<TeamChatDmPage />} />
                      <Route path="team-chat/case-chats" element={<TeamChatCaseChatsPage />} />
                      <Route path="team-chat/community" element={<CommunityRootRedirect />} />
                      <Route path="community" element={<CommunityRootRedirect />} />
                      <Route path="community/join/:token" element={<CommunityJoinPage />} />
                      <Route
                        path="ai-assistant"
                        element={
                          <PageAccessRoute>
                            <AIAssistantPage />
                          </PageAccessRoute>
                        }
                      />
                      
                      <Route
                        path="ai-usage"
                        element={
                          <PageAccessRoute>
                            <AIUsageAdminPage />
                          </PageAccessRoute>
                        }
                      />
                      <Route path="contact-chats" element={<ContactChatsPage />} />
                      <Route path="my-calls" element={<MyCallsPage />} />
                      <Route path="payments/razorpay-links" element={<RazorpayPaymentLinksPage />} />
                      <Route path="payments" element={<PaymentsPage />} />
                      <Route path="payouts" element={<OutflowPage />} />
                      <Route path="expenses" element={<ExpensesToTransactionsRedirect />} />
                      <Route path="outflow" element={<Navigate to="/app/payouts" replace />} />
                      <Route path="documents" element={<DocumentsPage />} />
                      <Route path="documents/ocr-queue" element={<ClientDocOcrQueuePage />} />
                      <Route path="documents/ingest-queue" element={<DocumentIngestQueuePage />} />
                      <Route path="receipt-documents" element={<Navigate to="/app/ledger/supporting-documents" replace />} />
                      <Route path="ledger/supporting-documents" element={<SupportingDocumentsPage />} />
                      <Route path="ledger/supporting-documents/ocr-queue" element={<OcrQueuePage />} />
                      <Route path="documents/trash" element={<DocumentsTrashPage />} />
                      <Route path="ledger" element={<LedgerDashboardPage />} />
                      <Route path="ledger/general-ledger" element={<GeneralLedgerPage />} />
                      <Route path="ledger/gl-accounts" element={<GLAccountsPage />} />
                      <Route path="ledger/gl-accounts/manage" element={<ManageGLAccountsPage />} />
                      <Route path="ledger/chart-of-accounts" element={<ChartOfAccountsPage />} />
                      <Route path="ledger/account-ledger" element={<AccountLedgerPage />} />
                      <Route path="ledger/journals" element={<JournalListPage />} />
                      <Route path="ledger/journals/:journalId" element={<JournalDetailPage />} />
                      <Route path="ledger/open-items" element={<AccountingExceptionsPage />} />
                      <Route path="ledger/accounting-exceptions" element={<AccountingExceptionsRedirect />} />
                      <Route path="ledger/bank-statements" element={<BankStatementPage />} />
                      <Route path="ledger/reconciliation" element={<ReconciliationPage />} />
                      <Route path="ledger/credit-card-statements" element={<CreditCardStatementsPage />} />
                      <Route path="ledger/credit-card-reconciliation" element={<CreditCardReconciliationPage />} />
                      <Route path="ledger/accounts" element={<AccountsManagementPage />} />
                      <Route path="ledger/integrations" element={<IntegrationsPage />} />
                      <Route
                        path="ledger/companies"
                        element={
                          <AdminRoute>
                            <CompanyManagementPage />
                          </AdminRoute>
                        }
                      />
                      <Route path="ledger/categories" element={<ManageGLAccountsPage />} />
                      <Route path="ledger/payees" element={<PayeeManagementPage />} />
                      <Route path="ledger/page-access" element={<LedgerPageAccessPage />} />
                      <Route path="ledger/recurring-vendor-checklist" element={<RecurringVendorChecklistPage />} />
                      <Route path="ledger/statements" element={<StatementsRegisterPage />} />
                      <Route path="ledger/loans" element={<LoanRegisterLegacyRedirect />} />
                      <Route
                        path="ledger/interac-payment-claims"
                        element={<Navigate to="/app/collections" replace />}
                      />
                      <Route path="collections/payment-links" element={<CollectionsPaymentLinksPage />} />
                      <Route path="collections" element={<InteracPaymentClaimsPage />} />
                      <Route path="partner-collections" element={<PartnerCollectionsPage />} />
                      <Route path="ledger/loans/given" element={<LoanRegisterLegacyRedirect />} />
                      <Route path="ledger/loans/received" element={<LoanRegisterLegacyRedirect />} />
                      <Route path="ledger/loans/by-entity" element={<LoanRegisterLegacyRedirect />} />
                      <Route path="ledger/accounts-receivable" element={<AccountsReceivablePage />} />
                      <Route path="ledger/accounts-payable" element={<AccountsPayablePage />} />
                      <Route path="ledger/books/trial-balance" element={<TrialBalancePage />} />
                      <Route path="ledger/books/income-statement" element={<IncomeStatementPage />} />
                      <Route path="ledger/books/balance-sheet" element={<BalanceSheetPage />} />
                      <Route path="ledger/financial-statement-generator" element={<FinancialStatementGeneratorPage />} />
                      <Route
                        path="ledger/financial-statement-generator/edit"
                        element={<FinancialStatementEditorPage />}
                      />
                      <Route
                        path="ledger/financial-statement-generator/edit/:templateId"
                        element={<FinancialStatementEditorPage />}
                      />
                      <Route path="ledger/gst-forecast" element={<MonthlyGstHstBreakdownPage />} />
                      <Route path="ledger/forecast" element={<ForecastPage />} />
                      <Route
                        path="ledger/financial-statement-generator/gst-hst"
                        element={<LegacyGstForecastRedirect />}
                      />
                      <Route path="ledger/books/accounts-receivable" element={<AccountsReceivablePage />} />
                      <Route path="ledger/books/accounts-payable" element={<AccountsPayablePage />} />
                      <Route path="ledger/books/gst-hst-remittance" element={<LegacyGstForecastRedirect />} />
                      <Route path="ledger/books/salary-contractor-payments" element={<SalaryContractorPaymentsPage />} />
                      <Route path="ledger/books/salary-slips" element={<SalarySlipsLayoutPage />}>
                        <Route index element={<PayrollPaySlipsListPage />} />
                        <Route path="create" element={<SalaryPayrollCalculatorPage />} />
                        <Route path="payroll-tax" element={<PayrollTaxRemittancePage />} />
                      </Route>
                      <Route path="ledger/books/salary-payroll" element={<LegacySalaryPayrollRedirect />} />
                      <Route path="ledger/books/payroll-payslips" element={<LegacyPayrollPayslipsRedirect />} />
                      <Route path="ledger/billing" element={<LedgerBillingLegacyRedirect />} />
                      <Route path="ledger/vouchers" element={<VoucherRootRedirect />} />
                      <Route path="ledger/vouchers/:voucherTab" element={<VoucherHubPage />} />
                      <Route path="ledger/financial-documents/vouchers" element={<LegacyFinancialDocumentsVouchersRedirect />} />
                      <Route path="ledger/financial-documents" element={<FinancialDocumentsLayoutPage />}>
                        <Route index element={<FinancialDocumentsIndexRedirect />} />
                        <Route path="invoices" element={<FinancialDocumentsInvoicesPage />} />
                        <Route path="receipts" element={<FinancialDocumentsReceiptsPage />} />
                      </Route>
                      <Route path="notifications" element={<NotificationsPage />} />
                      <Route path="mailbox/:threadId?" element={<MailboxPage />} />
                      <Route path="meetings" element={<MeetingsPage />} />
                      <Route path="appointments" element={<CalendarTablePage />} />
                      <Route path="appointments/calendar" element={<CalendarPage />} />
                      <Route path="appointments/unavailability" element={<UnavailabilityPage />} />
                      <Route path="contact-center" element={<ContactCenterPage />} />
                      <Route path="contact-center/action-board" element={<SupervisorActionBoardPage />} />
                      <Route path="contact-center/logs" element={<Navigate to="/app/contact-center/logs/incoming" replace />} />
                      <Route path="contact-center/logs/incoming" element={<CallLogPage />} />
                      <Route path="contact-center/logs/outgoing" element={<OutgoingCallLogsPage />} />
                      <Route path="contact-center/ops" element={<OpsDashboardPage />} />
                      <Route path="extensions" element={<ExtensionsPage />} />
                      <Route path="walk-ins" element={<WalkInsPage />} />
                      <Route path="conversions" element={<ConversionsPage />} />
                      <Route path="walk-ins/daily-activity-log" element={<DailyActivityLogPage />} />
                      <Route path="walk-ins/daily-work-report" element={<Navigate to="/app/reports/daily-work-report" replace />} />
                      <Route path="walk-ins/agent-reports" element={<WalkInAgentReportsPage />} />
                      <Route path="walk-ins/office-reports" element={<WalkInOfficeReportsPage />} />
                      <Route path="forms" element={<FormsPage />} />
                      <Route path="forms-management" element={<FormsManagementPage />} />
                      <Route path="form-builder/:formId" element={<FormBuilderPage />} />
                      {/* Legacy sheets routes removed */}
                      <Route path="orders-dashboard/daily-work-report" element={<Navigate to="/app/reports/daily-work-report" replace />} />
                      <Route path="orders-dashboard/daily-conversion-log" element={<Navigate to="/app/reports/daily-conversion-log" replace />} />
                      <Route path="orders-dashboard/tax" element={<OrderSectionPlaceholderPage />} />
                      <Route path="orders-dashboard/refugee" element={<RefugeeControlTowerPage />} />
                      <Route path="orders-dashboard/ss" element={<SsOpsPage />} />
                      <Route path="orders-dashboard/tr" element={<TrControlTowerPage />} />
                      <Route path="service-orders/*" element={<ServiceOrdersLegacyRedirect />} />
                      <Route path="shipping" element={<ShippingPage />} />
                      <Route path="manage-services" element={<ManageServicesPage />} />
                      <Route path="manage-users" element={<ManageUsersPage />} />
                      <Route path="hr-profiles" element={<HrProfilesPage />} />
                      <Route path="hr-profiles/:userId" element={<HrProfileDetailPage />} />
                      <Route path="hr-attendance" element={<HRAttendancePage />} />
                      <Route path="hr-training" element={<HRTrainingPage />} />
                      <Route path="hr-training/:trainingId" element={<HRTrainingDetailPage />} />
                      <Route path="hr-policy" element={<HRPolicyPage />} />
                      <Route
                        path="hr-policy/cases/:caseId"
                        element={<WorkComplianceCaseDetailPage />}
                      />
                      <Route path="hr-policy/:policyId" element={<HRPolicyDetailPage />} />
                      <Route path="whatsapp-settings" element={<WhatsAppSettingsPage />} />

                      <Route path="office-management" element={<OfficeManagementPage />} />
                      <Route path="departments" element={<DepartmentListPage />} />
                      <Route path="departments/new" element={<CreateDepartmentPage />} />
                      <Route path="departments/:departmentId" element={<DepartmentDetailPage />} />
                      <Route
                        path="legal-tasks"
                        element={
                          <PageAccessRoute>
                            <LegalTasksDashboardPage />
                          </PageAccessRoute>
                        }
                      />
                      <Route
                        path="legal-tasks/:taskId"
                        element={
                          <PageAccessRoute>
                            <LegalTaskDetailPage />
                          </PageAccessRoute>
                        }
                      />
                      <Route
                        path="my-tasks"
                        element={
                          <PageAccessRoute>
                            <MyTasksPage />
                          </PageAccessRoute>
                        }
                      />
                      <Route
                        path="my-attendance"
                        element={
                          <PageAccessRoute>
                            <MyAttendancePage />
                          </PageAccessRoute>
                        }
                      />
                      <Route
                        path="my-training"
                        element={
                          <PageAccessRoute>
                            <MyTrainingPage />
                          </PageAccessRoute>
                        }
                      />
                      <Route
                        path="my-training/:trainingId"
                        element={
                          <PageAccessRoute>
                            <MyTrainingDetailPage />
                          </PageAccessRoute>
                        }
                      />
                      <Route
                        path="my-policies"
                        element={
                          <PageAccessRoute>
                            <MyPoliciesPage />
                          </PageAccessRoute>
                        }
                      />
                      <Route
                        path="my-policies/:assignmentId"
                        element={
                          <PageAccessRoute>
                            <MyPolicyDetailPage />
                          </PageAccessRoute>
                        }
                      />
                      <Route
                        path="my-compliance"
                        element={
                          <PageAccessRoute>
                            <MyCompliancePage />
                          </PageAccessRoute>
                        }
                      />
                      <Route
                        path="my-compliance/:caseId"
                        element={
                          <PageAccessRoute>
                            <MyComplianceDetailPage />
                          </PageAccessRoute>
                        }
                      />
                      <Route path="so/:id" element={<ServiceOrderDetailPage />} />
                      <Route path="ld/:id" element={<LeadDetailPage />} />
                      <Route path="s/:id" element={<ServiceDetailPage />} />
                      <Route path="users/:id" element={<UserDetailPage />} />
                      <Route path="profile" element={<ProfilePage />} />
                      <Route path="connect/continue" element={<ConnectContinuePage />} />
                      <Route path="*" element={<ErrorPage />} />
                    </Route>
                    <Route path="/" element={<SmartRedirect><div>Redirecting...</div></SmartRedirect>} />
                    <Route path="*" element={<ErrorPage />} />
        </Routes>
      </Suspense>
    </>
  );
};

export default App;

//add comment to redploy
